import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cashoutSpend, cashoutSave, getBalance, getBankAccounts } from '../utils/api';
import { usePlatform } from '../context/PlatformContext';

export default function CashoutPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Cash Out';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const navigate = useNavigate();
  const platform = usePlatform();
  const [tab, setTab]                   = useState('spend');
  const [balance, setBalance]           = useState(null);
  const [amount, setAmount]             = useState('');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState(null);
  const [taxAcknowledged, setTaxAcknowledged] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [dropOpen, setDropOpen]         = useState(false);

  useEffect(() => {
    getBalance().then(r => setBalance(r.data)).catch(console.error);
    getBankAccounts().then(r => {
      setBankAccounts(r.data);
      const primary = r.data.find(a => a.isPrimary) || r.data[0];
      if (primary) setSelectedBankId(primary.id);
    }).catch(console.error);
  }, []);

  const amountCents = Math.round(parseFloat(amount||0)*100);
  const feeCents    = Math.round(amountCents * (platform.feeBps / 10000));
  const netCents    = amountCents - feeCents;
  const selectedBank = bankAccounts.find(a => a.id === selectedBankId);

  async function handleCashout() {
    if (amountCents < platform.minCashoutCents) {
      setError(platform.minCashoutCents > 0
        ? `Minimum cashout is $${(platform.minCashoutCents/100).toFixed(2)}`
        : 'Please enter an amount');
      return;
    }
    if (tab === 'stack' && !taxAcknowledged) {
      setError('Please acknowledge the tax notice before cashing out.');
      return;
    }
    if (bankAccounts.length === 0) {
      setError('Please add a bank account in Settings first.');
      return;
    }
    setError(''); setLoading(true);
    try {
      if (tab === 'spend') {
        await cashoutSpend(amountCents, selectedBankId);
        setSuccess({ type: 'bank', netCents, tab });
      } else {
        const satoshis = Math.round((amountCents/100)/75000*100000000);
        await cashoutSave(satoshis, selectedBankId);
        setSuccess({ type: 'bank', netCents, tab });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    } finally { setLoading(false); }
  }

  if (success) return (
    <div style={s.page}>
      <div style={s.successWrap}>
        <div style={s.successIcon}>✓</div>
        <div style={s.successTitle}>${(success.netCents/100).toFixed(2)} on the way</div>
        <div style={s.successSub}>
          {success.tab === 'spend'
            ? 'Arriving in your bank in 1–2 business days'
            : 'BTC will be sold and USD sent to your bank'}
        </div>
        {success.tab === 'stack' && (
          <div style={s.taxReminder}>Remember to report this sale on your tax return.</div>
        )}
        <button style={s.btn} onClick={() => navigate('/dashboard')}>Done</button>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={() => navigate('/dashboard')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <span style={s.headerTitle}>Cash Out</span>
        <div style={{width:36}}/>
      </div>

      <div style={s.body}>

        {/* Source tabs */}
        <div style={s.segment}>
          {['spend','stack'].map(t => (
            <button key={t}
              style={{...s.segBtn, ...(tab===t ? s.segBtnActive : {})}}
              onClick={() => { setTab(t); setAmount(''); setError(''); setTaxAcknowledged(false); }}>
              {t === 'spend' ? 'Spend Balance' : 'Bitcoin Stack'}
            </button>
          ))}
        </div>

        {/* Available balance */}
        <div style={s.balanceInfo}>
          <span style={s.balLabel}>Available</span>
          <span style={s.balValue}>
            ${tab === 'spend' ? balance?.spend?.usd||'0.00' : balance?.save?.usd||'0.00'}
          </span>
        </div>

        {/* Amount */}
        <div style={s.amountCard}>
          <label style={s.fieldLabel}>Amount</label>
          <div style={s.amountRow}>
            <span style={s.dollar}>$</span>
            <input style={s.amountInput} type="number" placeholder="0.00"
              min="1" step="0.01" value={amount}
              onChange={e => { setAmount(e.target.value); setError(''); }}/>
          </div>
        </div>

        {/* Bank account dropdown */}
        <div style={s.sectionLabel}>Bank Account</div>
        {bankAccounts.length === 0 ? (
          <div style={s.noBankWarning}>
            No bank account added.{' '}
            <span style={{color:'#007AFF', cursor:'pointer', fontWeight:500}}
              onClick={() => navigate('/banks')}>
              Add one in Settings →
            </span>
          </div>
        ) : (
          <div style={{position:'relative', marginBottom:16}}>
            <button style={s.dropWrap} onClick={() => setDropOpen(o => !o)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="1.8" strokeLinecap="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                <path d="M9 22V12h6v10"/>
              </svg>
              <div style={{flex:1, textAlign:'left'}}>
                <div style={s.dropBankName}>
                  {selectedBank ? `${selectedBank.bankName || 'Bank'} •••${selectedBank.last4}` : 'Select bank'}
                </div>
                <div style={s.dropBankSub}>
                  {selectedBank ? selectedBank.accountType : ''}
                  {selectedBank?.isPrimary && <span style={s.primaryBadge}>Primary</span>}
                </div>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6E6E73" strokeWidth="2.5"
                style={{flexShrink:0, transform: dropOpen ? 'rotate(180deg)' : 'none', transition:'transform 0.2s'}}>
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </button>

            {dropOpen && (
              <>
                <div style={s.dropOverlay} onClick={() => setDropOpen(false)}/>
                <div style={s.dropMenu}>
                  {bankAccounts.map((account, i) => (
                    <button
                      key={account.id}
                      style={{
                        ...s.dropItem,
                        ...(selectedBankId === account.id ? s.dropItemActive : {}),
                        borderBottom: i < bankAccounts.length - 1 ? '1px solid #F5F5F7' : 'none',
                      }}
                      onClick={() => { setSelectedBankId(account.id); setDropOpen(false); }}
                    >
                      <div style={{flex:1, textAlign:'left'}}>
                        <div style={{fontSize:14, fontWeight:500}}>
                          {account.bankName || 'Bank'} •••{account.last4}
                        </div>
                        <div style={{fontSize:12, color:'#6E6E73', marginTop:2, textTransform:'capitalize'}}>
                          {account.accountType}
                          {account.isPrimary && <span style={{...s.primaryBadge, marginLeft:6}}>Primary</span>}
                        </div>
                      </div>
                      {selectedBankId === account.id && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="2.5">
                          <path d="M20 6L9 17l-5-5"/>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* BTC notice */}
        {tab === 'stack' && (
          <div style={s.notice}>
            Bitcoin is sold at market rate. Final USD amount may vary slightly.
          </div>
        )}

        {/* Summary */}
        <div style={s.summaryCard}>
          <div style={s.summaryRow}>
            <span style={s.summaryLabel}>Service fee ({platform.feePct}%)</span>
            <span style={s.summaryValue}>${(feeCents/100).toFixed(2)}</span>
          </div>
          <div style={s.summaryRow}>
            <span style={s.summaryLabel}>Arrival</span>
            <span style={s.summaryValue}>1–2 business days</span>
          </div>
          <div style={s.divider}/>
          <div style={s.summaryRow}>
            <span style={{...s.summaryLabel, fontWeight:600, color:'#1D1D1F'}}>You receive</span>
            <span style={{...s.summaryValue, fontWeight:600, color:'#1D1D1F'}}>${(netCents/100).toFixed(2)}</span>
          </div>
        </div>

        {/* Tax warning */}
        {tab === 'stack' && amountCents > 0 && (
          <div style={s.taxWarning}>
            <div style={s.taxTitle}>⚠️ Tax notice</div>
            <div style={s.taxText}>
              Cashing out Bitcoin stack is a taxable event. You may owe capital gains tax on any profit.
            </div>
            <div style={s.taxText}>
              Short-term gains (under 1 year) taxed as ordinary income. Long-term gains (over 1 year) taxed at 0–20%.
            </div>
            <div style={s.taxAckRow}>
              <input type="checkbox" id="taxAck" checked={taxAcknowledged}
                onChange={e => setTaxAcknowledged(e.target.checked)} style={s.checkbox}/>
              <label htmlFor="taxAck" style={s.taxAckLabel}>
                I understand this is a taxable event
              </label>
            </div>
          </div>
        )}

        {error && <div style={s.error}>{error}</div>}

        <button
          style={{
            ...s.btn,
            background: tab === 'stack' ? '#00C48C' : '#007AFF',
            opacity: (tab==='stack' && amountCents>0 && !taxAcknowledged) ? 0.5 : 1
          }}
          onClick={handleCashout}
          disabled={loading}>
          {loading ? 'Processing...' : `Cash out $${(amountCents/100).toFixed(2)} to bank`}
        </button>

      </div>
    </div>
  );
}

const s = {
  page:          {minHeight:'100vh', background:'#F5F5F7', paddingBottom:88},
  header:        {display:'flex', alignItems:'center', justifyContent:'space-between', padding:'56px 20px 16px'},
  back:          {width:36, height:36, borderRadius:10, background:'#fff', border:'none', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.08)'},
  headerTitle:   {fontSize:17, fontWeight:600, color:'#1D1D1F'},
  body:          {padding:'0 16px'},
  segment:       {display:'flex', background:'#E5E5EA', borderRadius:12, padding:3, gap:2, marginBottom:16},
  segBtn:        {flex:1, padding:'9px 12px', border:'none', borderRadius:10, fontSize:13, fontWeight:500, color:'#6E6E73', background:'transparent', cursor:'pointer', transition:'all 0.2s'},
  segBtnActive:  {background:'#fff', color:'#1D1D1F', fontWeight:600, boxShadow:'0 1px 4px rgba(0,0,0,0.12)'},
  balanceInfo:   {display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0 4px', marginBottom:12},
  balLabel:      {fontSize:13, color:'#6E6E73'},
  balValue:      {fontSize:15, fontWeight:600},
  amountCard:    {background:'#fff', borderRadius:20, padding:20, marginBottom:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  fieldLabel:    {fontSize:12, fontWeight:500, color:'#6E6E73', display:'block', marginBottom:8},
  amountRow:     {display:'flex', alignItems:'center', gap:4},
  dollar:        {fontSize:36, fontWeight:600},
  amountInput:   {fontSize:36, fontWeight:600, border:'none', background:'transparent', width:'100%', outline:'none'},
  sectionLabel:  {fontSize:12, fontWeight:600, color:'#AEAEB2', marginBottom:10, textTransform:'uppercase', letterSpacing:0.8},
  dropWrap:      {display:'flex', alignItems:'center', gap:12, background:'#fff', borderRadius:16, padding:'14px 16px', boxShadow:'0 2px 20px rgba(0,0,0,0.06)', border:'none', cursor:'pointer', width:'100%'},
  dropBankName:  {fontSize:15, fontWeight:600, color:'#1D1D1F'},
  dropBankSub:   {fontSize:12, color:'#6E6E73', marginTop:2, textTransform:'capitalize', display:'flex', alignItems:'center', gap:6},
  dropOverlay:   {position:'fixed', inset:0, zIndex:99},
  dropMenu:      {position:'absolute', top:'calc(100% + 8px)', left:0, right:0, background:'#fff', borderRadius:16, boxShadow:'0 8px 40px rgba(0,0,0,0.14)', zIndex:100, overflow:'hidden'},
  dropItem:      {display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', padding:'14px 16px', background:'none', border:'none', cursor:'pointer'},
  dropItemActive:{background:'#F0F7FF'},
  primaryBadge:  {background:'#EBF4FF', color:'#007AFF', fontSize:10, fontWeight:600, padding:'2px 6px', borderRadius:6},
  noBankWarning: {background:'#FFF9EC', border:'1px solid #FFE8A3', borderRadius:12, padding:'12px 14px', fontSize:13, color:'#B87A00', marginBottom:16},
  notice:        {background:'#FFF9EC', border:'1px solid #FFE8A3', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#B87A00', marginBottom:12},
  summaryCard:   {background:'#fff', borderRadius:16, padding:16, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  summaryRow:    {display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0'},
  summaryLabel:  {fontSize:14, color:'#6E6E73'},
  summaryValue:  {fontSize:14, color:'#6E6E73'},
  divider:       {height:1, background:'#F5F5F7', margin:'10px 0'},
  taxWarning:    {background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:12, padding:'14px', marginBottom:12},
  taxTitle:      {fontSize:13, fontWeight:600, color:'#92400E', marginBottom:8},
  taxText:       {fontSize:12, color:'#92400E', lineHeight:1.6, marginBottom:6},
  taxAckRow:     {display:'flex', alignItems:'center', gap:8, marginTop:10},
  checkbox:      {width:16, height:16, accentColor:'#007AFF', cursor:'pointer'},
  taxAckLabel:   {fontSize:13, fontWeight:500, color:'#92400E', cursor:'pointer'},
  error:         {background:'#FFF0EF', border:'1px solid #FFD0CE', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#FF3B30', marginBottom:12},
  btn:           {width:'100%', padding:16, color:'#fff', border:'none', borderRadius:14, fontSize:16, fontWeight:600, letterSpacing:-0.3, transition:'opacity 0.2s', marginBottom:8},
  successWrap:   {display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', padding:24, gap:12, textAlign:'center'},
  successIcon:   {width:64, height:64, borderRadius:'50%', background:'#E6FAF5', color:'#00C48C', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28},
  successTitle:  {fontSize:24, fontWeight:600},
  successSub:    {fontSize:14, color:'#6E6E73', lineHeight:1.5},
  taxReminder:   {background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:12, padding:'12px 14px', fontSize:12, color:'#92400E', lineHeight:1.5, marginBottom:16},
};
